import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import { FaChartLine, FaArrowRight, FaShieldAlt, FaStar, FaExchangeAlt, FaFire, FaMoneyBillWave, FaSeedling, FaRocket, FaFileDownload, FaPercentage } from 'react-icons/fa';
import { motion } from 'framer-motion';
import axios from 'axios';

const PortfolioHome = () => {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Icon mapping based on portfolio type or index
  const getIconForPortfolio = (index) => {
    const icons = [
      <FaChartLine />,
      <FaStar />,
      <FaPercentage />,
      <FaSeedling />,
      <FaFire />,
      <FaChartLine />
    ];
    return icons[index % icons.length];
  };

  // Gradient mapping
  const getGradientForPortfolio = (index) => {
    const gradients = [
      "from-blue-600 to-blue-600",
      "from-blue-600 to-blue-600",
      "from-blue-600 to-blue-600",
      "from-blue-600 to-blue-600",
      "from-blue-600 to-blue-600",
      "from-blue-600 to-blue-600"
    ];
    return gradients[index % gradients.length];
  };

  // Stats mapping based on portfolio content
  const getStatsForPortfolio = (portfolio) => {
    if (portfolio.title?.toLowerCase().includes('dividend')) return "4.2% Avg Yield";
    if (portfolio.title?.toLowerCase().includes('long term')) return "22% CAGR Target";
    if (portfolio.title?.toLowerCase().includes('screener')) return "100+ Metrics Tracked";
    if (portfolio.title?.toLowerCase().includes('heat map')) return "Real-Time Market View";
    if (portfolio.title?.toLowerCase().includes('weather')) return "15.8% Avg Return";
    return "Updated Weekly";
  };

  // Fetch portfolio data from API
  useEffect(() => {
    fetchPortfolioData();
  }, []);

  const fetchPortfolioData = async () => {
    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.get(`${apiUrl}/api/portfolio-section`);
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        // Transform the data into an array of portfolio items
        const portfolioItems = [];
        for (let i = 1; i <= 6; i++) {
          const portKey = `port${i}`;
          if (data[`${portKey}_title`]) { // Only add if title exists
            portfolioItems.push({
              id: i,
              title: data[`${portKey}_title`] || '',
              subtitle: data[`${portKey}_subtitle`] || '',
              description: data[`${portKey}_shortdesc`] || '',
              image: data[`${portKey}_image`] || '',
              link: data[`${portKey}_link`] || `/portfolio/item-${i}`,
              buttonText: "View More",
              gradient: getGradientForPortfolio(i - 1),
              stats: getStatsForPortfolio({ title: data[`${portKey}_title`] }),
              icon: getIconForPortfolio(i - 1)
            });
          }
        }
        
        setPortfolios(portfolioItems);
      }
    } catch (error) {
      console.error('Error fetching portfolio data:', error);
      setError('Failed to load portfolio data');
      
      // Fallback to default data if API fails
      setPortfolios([
        {
          id: 1,
          title: "Registered RA Platform",
          subtitle: "Live Stock Analysis & IPO Tracking",
          description: "Live stock recommendations with Ticker, Stock Name, Reco Price, CMP, Return %, Stop Loss, Target. Plus latest IPO listings with analysis.",
          image: "/api/placeholder/400/200",
          link: "/portfolio/registered-ra-platform",
          buttonText: "View More",
          gradient: "from-blue-600 to-blue-600",
          stats: "Updated Weekly",
          icon: <FaChartLine />
        },
        {
          id: 2,
          title: "All Weather Portfolio",
          subtitle: "Diversified Market Strategy",
          description: "Diversified stock recommendations designed to perform in all market conditions with live returns tracking.",
          image: "/api/placeholder/400/200",
          link: "/portfolio/all-weather-portfolio",
          buttonText: "View More",
          gradient: "from-emerald-600 to-emerald-600",
          stats: "15.8% Avg Return",
          icon: <FaStar />
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Default tags for all portfolios
  const defaultTags = ["Live Tracking", "Detailed Analysis", "SEBI Approved"];

  if (loading) {
    return (
      <section className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
            <p className="text-gray-300 mt-4">Loading portfolios...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error && portfolios.length === 0) {
    return (
      <section className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-red-400">Failed to load portfolio data. Please try again later.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
          className="absolute top-10 left-10 w-96 h-96 border-4 border-blue-500/30 rounded-full"
        />
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-10 right-10 w-80 h-80 border-4 border-emerald-500/30 rounded-full"
        />
      </div>

      {/* Floating Elements */}
      <motion.div
        animate={{ y: [0, -40, 0] }}
        transition={{ duration: 6, repeat: Infinity }}
        className="absolute top-20 left-1/4 text-6xl opacity-10"
      >
        📊
      </motion.div>
      <motion.div
        animate={{ y: [0, 30, 0] }}
        transition={{ duration: 7, repeat: Infinity, delay: 1 }}
        className="absolute top-1/3 right-1/4 text-5xl opacity-10"
      >
        📈
      </motion.div>

      {/* Animated Dots Pattern */}
      <div className="absolute top-10 right-20 hidden xl:grid grid-cols-6 gap-2 opacity-10">
        {[...Array(36)].map((_, i) => (
          <motion.div
            key={i}
            animate={{ scale: [1, 1.5, 1] }}
            transition={{ duration: 2, repeat: Infinity, delay: i * 0.05 }}
            className="w-1 h-1 bg-blue-400 rounded-full"
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <motion.div
            className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm border border-blue-400/30 px-6 py-3 rounded-full mb-6"
          >
            <FaStar className="text-blue-400" />
            <span className="text-blue-300 font-bold text-sm tracking-wider">PREMIUM PORTFOLIOS</span>
          </motion.div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Expert Curated Portfolios
          </h2>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto">
            Access SEBI Registered Analyst approved portfolios with live tracking and detailed analysis
          </p>
        </motion.div>

        {/* Portfolio Grid - 3 cards per row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {portfolios.map((portfolio, index) => (
            <motion.div
              key={portfolio.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="relative group"
            >
              {/* Card Container */}
              <div className="relative h-full bg-gray-800/60 backdrop-blur-sm rounded-2xl shadow-2xl hover:shadow-blue-500/20 hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-700/50 hover:border-gray-600">
                
                {/* Card Image Header */}
                <div className="relative h-48 overflow-hidden">
                  {portfolio.image ? (
                    <motion.img
                      src={`${process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in'}${portfolio.image}`}
                      alt={portfolio.title}
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.3 }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
                      <span className="text-white text-4xl">{portfolio.icon}</span>
                    </div>
                  )}
                  {/* Image overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/30 to-transparent"></div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  {/* Title and Subtitle */}
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-white mb-1 line-clamp-1">
                      {portfolio.title}
                    </h3>
                    <p className="text-gray-400 text-sm mb-3">
                      {portfolio.subtitle}
                    </p>
                  </div>

                  {/* Description */}
                  <p className="text-gray-300 text-sm mb-6 line-clamp-3">
                    {portfolio.description}
                  </p>

                  {/* Features/Tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {defaultTags.map((tag, idx) => (
                      <motion.span
                        key={idx}
                        whileHover={{ scale: 1.05 }}
                        className="px-3 py-1 bg-gray-700/50 text-gray-300 text-xs font-medium rounded-full border border-gray-600/50"
                      >
                        {tag}
                      </motion.span>
                    ))}
                  </div>

                  {/* Stats Badge */}
                  {/* <div className="mb-4">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full">
                      {portfolio.icon}
                      <span>{portfolio.stats}</span>
                    </span>
                  </div> */}

                  {/* Access Now Button */}
                  <Link to={portfolio.link} target="_blank" rel="noopener noreferrer">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`w-full py-3 bg-gradient-to-r ${portfolio.gradient} text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 group relative overflow-hidden`}
                    >
                      <span className="relative z-10">{portfolio.buttonText}</span>
                      <FaArrowRight className="relative z-10 group-hover:translate-x-1 transition-transform" />
                    </motion.button>
                  </Link>
                </div>
              </div>

              {/* Floating Badge for first portfolio or based on condition */}
              {portfolio.id === 1 && (
                <motion.div
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute -top-3 -left-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full p-2 shadow-lg z-10 border border-blue-400/50"
                >
                  <FaShieldAlt className="text-white text-sm" />
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PortfolioHome;